import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoUploader } from '../PhotoUploader';
import { PhotoGalleryEditor } from '../PhotoGalleryEditor';
import { ConfirmDialog } from '../ConfirmDialog';
import { useUpdatePhotos } from '../../hooks/useApartments';
import { is404 } from '../../utils/errors';
import type { Apartment, PhotoDto, PhotoRequest } from '../../types/apartment';

interface PhotosSectionProps {
  readonly apartment: Apartment;
}

export const PhotosSection = ({ apartment }: PhotosSectionProps) => {
  const { t } = useTranslation(['common', 'forms', 'errors', 'pages']);
  const updatePhotosMutation = useUpdatePhotos();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmLastPhoto, setConfirmLastPhoto] = useState<{
    open: boolean;
    photo: PhotoDto | null;
  }>({ open: false, photo: null });

  const currentPhotos = apartment.photos;

  const doDeletePhoto = async (photoToRemove: PhotoDto) => {
    const remaining: PhotoRequest[] = currentPhotos
      .filter(p => p.key !== photoToRemove.key)
      .map((p, index) => ({
        key: p.key,
        photoUrl: p.photoUrl,
        thumbnailUrl: p.thumbnailUrl,
        optimizedUrl: p.optimizedUrl,
        uploadedAt: p.uploadedAt,
        displayOrder: index,
      }));
    setDeleteError(null);
    try {
      await updatePhotosMutation.mutateAsync({ id: apartment.id, photos: remaining });
    } catch (err) {
      setDeleteError(
        is404(err) ? t('errors:apartmentNotFound') : t('errors:somethingWentWrong')
      );
    }
  };

  const handleDeletePhoto = (photoToRemove: PhotoDto) => {
    if (currentPhotos.length === 1) {
      setConfirmLastPhoto({ open: true, photo: photoToRemove });
      return;
    }
    void doDeletePhoto(photoToRemove);
  };

  const handlePhotosChange = async (updatedPhotos: PhotoRequest[]) => {
    setDeleteError(null);
    try {
      await updatePhotosMutation.mutateAsync({ id: apartment.id, photos: updatedPhotos });
    } catch (err) {
      setDeleteError(
        is404(err) ? t('errors:apartmentNotFound') : t('errors:somethingWentWrong')
      );
    }
  };

  const handlePhotoUploaded = (photo: { key: string; photoUrl: string; thumbnailUrl: string; optimizedUrl: string; displayOrder: number }) => {
    const updated: PhotoRequest[] = [
      ...currentPhotos.map((p, i) => ({
        key: p.key,
        photoUrl: p.photoUrl,
        thumbnailUrl: p.thumbnailUrl,
        optimizedUrl: p.optimizedUrl,
        uploadedAt: p.uploadedAt,
        displayOrder: i,
      })),
      {
        key: photo.key,
        photoUrl: photo.photoUrl,
        thumbnailUrl: photo.thumbnailUrl,
        optimizedUrl: photo.optimizedUrl,
        displayOrder: photo.displayOrder,
      },
    ];
    void handlePhotosChange(updated);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('forms:photos')}</h2>

      <PhotoUploader
        apartmentId={apartment.id}
        onPhotoUploaded={handlePhotoUploaded}
        nextDisplayOrder={currentPhotos.length}
      />

      {currentPhotos.length > 0 && (
        <div className="mt-4">
          <PhotoGalleryEditor
            apartmentId={apartment.id}
            photos={currentPhotos as PhotoDto[]}
            onPhotosChange={(photos) => void handlePhotosChange(photos)}
            onDeletePhoto={handleDeletePhoto}
          />
        </div>
      )}

      {deleteError && (
        <p className="text-sm text-red-600 mt-3">{deleteError}</p>
      )}

      <ConfirmDialog
        open={confirmLastPhoto.open}
        message={t('pages:removeLastPhotoConfirm')}
        intent="danger"
        onConfirm={() => {
          setConfirmLastPhoto({ open: false, photo: null });
          if (confirmLastPhoto.photo) void doDeletePhoto(confirmLastPhoto.photo);
        }}
        onCancel={() => setConfirmLastPhoto({ open: false, photo: null })}
      />
    </div>
  );
};

export default PhotosSection;
